import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import Drawer from "../../../../components/Drawer";
import Form from "./Form";
import BulkActions from "../../components/BulkActions";
import Topbar from "../../components/Topbar";
import Actions from "../../../../components/NTable/Actions";
import NTable from "../../../../components/NTable";
import { useColumns } from "./columns";
import useSettingsCRUDHook from "../../hook";
import { useDrawerProps } from "../../helpers/useDrawerProps";
import { useDeleteModal } from "../../helpers/useDeleteModal";
import { useManageForm } from "../../helpers/useManageForm";
import { useFetchResource } from "../../helpers/useFetchResource";
import { useAccess, useActionWithAccess } from "../../../../utils/hooks/access";
import { useImportModal } from "../../helpers/useImportModal";
import { useOnExport } from "../../helpers/useOnExport";
import { useVisibleColumnKeys } from "../../helpers/useVisibleColumnKeys";
import { useQueryFilters } from "../../helpers/useQueryFilters";
import { useNewTable } from "../../../../components/NTable/hook";
import { useVisible } from "../../../../utils/hooks/useVisible";
import AllocateDrawer from "./Form/AllocateDrawer";
import HistoryDrawer from "./HistoryDrawer";
import VersionsModal from "./Versions";
import { useScheduledChangeEdit } from "./Versions/useScheduledChangeEdit";
import { scheduledFormValues } from "./Versions/scheduledChange";

function TimeOffRequestCode() {
    const { t } = useTranslation();

    const {
        state: { mode, data, totalItems, loading, submitting, error },
        selected,
        fetch,
        createSubmit,
        updateSubmit,
        goToHelp,
        goToView,
        goToCreate,
        goToEdit,
        goToClone,
        remove,
        batchRemove,
        settingsExport,
        loadingExport,
        setData,
    } = useSettingsCRUDHook("/timeoff");

    const { hasAccess: canAdd } = useAccess("settings.payPolicies.timeOff.canCreate");

    const {
        visible: isHistoryVisible,
        open: _openHistory,
        close: closeHistory,
        selected: selectedRowHistory
    } = useVisible();
    const openHistory = useActionWithAccess(_openHistory, "canViewAudits");

    const { modal: deleteModal, open: openDeleteModal } = useDeleteModal({
        message: (data) =>
            Array.isArray(data)
                ? t("do-you-want-to-delete-time-off-requests", {
                      count: data?.length,
                  })
                : t("do-you-want-to-delete-time-off-request"),
        onConfirm: (data) => (Array.isArray(data) ? batchRemove(data) : remove(data)),
        permission: "settings.payPolicies.timeOff.canDelete",
    });
    const { unarchiveModal, isFormOpen, formProps } = useManageForm({
        mode,
        selected,
        createSubmit,
        updateSubmit,
    });

    const {
        visible: isAllocateDrawerOpen,
        open: openAllocateDrawer,
        close: closeAllocateDrawer,
        selected: selectedRow,
    } = useVisible();

    const {
        visible: isVersionsOpen,
        open: openVersions,
        close: closeVersions,
        selected: selectedVersionsPlan,
    } = useVisible();

    // Edit asks first when the plan has a scheduled change; the form then gets the scheduled settings
    const {
        edit: editWithScheduledChange,
        modal: scheduledChangeModal,
        scheduled: editingScheduled,
    } = useScheduledChangeEdit({ goToEdit, mode, selectedId: selected?.id });

    const columns = useColumns({
        edit: useActionWithAccess(editWithScheduledChange, "settings.payPolicies.timeOff.canEdit"),
        remove: openDeleteModal,
        clone: useActionWithAccess(goToClone, "settings.payPolicies.timeOff.canCreate"),
        allocation: useActionWithAccess(
            openAllocateDrawer,
            "settings.payPolicies.timeOff.canEdit"
        ),
        history: openHistory,
        versions: openVersions,
    });

    const table = useNewTable({
        data,
        columns,
        loading,
        totalItems,
        sessionFiltersKey: "timeOffRequestCodes",
    });

    const queryFilters = useQueryFilters(table);

    const { open: drawerOpen, title: drawerTitle } = useDrawerProps({
        mode,
        help: t("help"),
        view: t("view-time-off-plan"),
        create: t("add-time-off-plan"),
        edit: t("edit-time-off-plan"),
        clone: t("clone-time-off-plan"),
    });

    useFetchResource({ fetch, state: table.getState() });

    // A save replaces its row with the response, which carries `scheduledChangeFrom` (the badge
    // on Versions); a discard in Versions clears it on that row. No list reload needed.
    const clearScheduledChange = useCallback(
        (planId) =>
            setData((rows) => rows.map((row) => (row.id === planId ? { ...row, scheduledChangeFrom: undefined } : row))),
        [setData],
    );

    const visibleColumnKeys = useVisibleColumnKeys(table);

    const onExport = useOnExport({
        url: "/export/time-off",
        exportCall: settingsExport,
        queryFilters,
        visibleColumnKeys,
    });

    const { open: openImportModal, modal: importModal } = useImportModal({
        importUrl: "/time-off/import",
        exportUrl: "/export/time-off",
        setData,
        exportCall: settingsExport,
        loading: loading || loadingExport,
    });

    return (
        <div className="d-flex h-100">
            <Card className="flex-fill mb-0">
                <Topbar
                    title={t("time-off-plans-settings-title")}
                    add={canAdd && goToCreate}
                    help={goToHelp}
                    importFile={openImportModal}
                    onExport={onExport}
                    loading={loading || loadingExport}
					history={!!openHistory && (() => openHistory())}
                />

                <Card className="mb-auto" style={{ maxHeight: "calc(100% - 156px)" }}>
                    <Actions
                        filters={table.getState().columnFilters}
                        setFilters={table.setColumnFilters}
                        columns={table.getAllColumns()}
                    >
                        <BulkActions
                            rows={table.getState().rowSelection}
                            batchRemove={openDeleteModal}
                        />
                    </Actions>

                    <NTable table={table} />
                </Card>
            </Card>

            <Drawer open={drawerOpen} close={goToView} title={drawerTitle}>
                {isFormOpen ? (
                    <Form
                        {...formProps}
                        values={
                            editingScheduled
                                ? scheduledFormValues(formProps.values, editingScheduled)
                                : formProps.values
                        }
                        scheduledFrom={editingScheduled?.startEffectiveDate}
                        error={error}
                        loading={submitting}
                        close={goToView}
                    />
                ) : (
                    <>{t("help")}</>
                )}
            </Drawer>

            {isAllocateDrawerOpen && (
                <AllocateDrawer
                    isOpen={isAllocateDrawerOpen}
                    planId={selectedRow}
                    close={closeAllocateDrawer}
                />
            )}

            {isHistoryVisible && (
                <HistoryDrawer open={isHistoryVisible} close={closeHistory} id={selectedRowHistory} />
            )}

            {isVersionsOpen && (
                <VersionsModal
                    isOpen={isVersionsOpen}
                    close={closeVersions}
                    plan={selectedVersionsPlan}
                    onDiscarded={() => clearScheduledChange(selectedVersionsPlan?.id)}
                />
            )}

            {importModal}

            {deleteModal}

            {scheduledChangeModal}

            {unarchiveModal}
        </div>
    );
}

export default TimeOffRequestCode;
